import React, { useCallback, useEffect, useState } from "react";
import { inject, observer } from "mobx-react";
import TailwindSwitch from "library/components/_tailwind/switch";
import TailwindDropdown, {
  TailwindDropdownValue,
} from "library/components/_tailwind/dropdown";
import CookieStorage from "library/core/utility/cookie-storage";
import { authStore } from "core/stores";
import { FEATURE_FLAGS } from "core/stores/auth/feature-flags";

type Props = {};

const UpcomingFeatures: React.ComponentType<Props> = ({}) => {
  const [bulkMessageOn, setBulkMessageOn] = useState<boolean>(false);
  const toggleBulkMessage = useCallback(() => {
    setBulkMessageOn(!bulkMessageOn);
    CookieStorage.set("bulkMessage", !bulkMessageOn);
  }, [bulkMessageOn]);

  const [mcPhase2MediaOn, setMCPhase2MediaOn] = useState<boolean>(false);
  const toggleMCPhase2Media = useCallback(() => {
    setMCPhase2MediaOn(!mcPhase2MediaOn);
    CookieStorage.set("MCPhase2Media", !mcPhase2MediaOn, {
      sameSite: "none",
      secure: true,
    });
    location.reload();
  }, [mcPhase2MediaOn]);

  const [liveTranslationOn, setLiveTranslationOn] = useState<boolean>(false);
  const toggleLiveTranslate = useCallback(() => {
    setLiveTranslationOn(!liveTranslationOn);
    CookieStorage.set("liveTranslation", !liveTranslationOn, {
      sameSite: "none",
      secure: true,
    });
    location.reload();
  }, [liveTranslationOn]);

  const isFaceBlurFeatureFlag = authStore.isFeatureEnabled(
    FEATURE_FLAGS.FEATURE_FACE_BLUR
  );

  const [faceBlurFeatureMode, setFaceBlurFeatureMode] = useState<string>("off");
  const onChangeFaceBlurFeature = useCallback(
    (value: TailwindDropdownValue) => {
      if (isFaceBlurFeatureFlag) return;
      const mode = value as string;
      setFaceBlurFeatureMode(mode);
      if (mode === "off") {
        CookieStorage.remove("faceBlurFeatureVisible");
      } else {
        CookieStorage.set("faceBlurFeatureVisible", mode, {
          sameSite: "none",
          secure: true,
        });
      }
      location.reload();
    },
    [isFaceBlurFeatureFlag]
  );

  useEffect(() => {
    const hasBulkMessageCookie = CookieStorage.get("bulkMessage");
    if (hasBulkMessageCookie) {
      setBulkMessageOn(!!hasBulkMessageCookie);
    }

    const mcPhase2MediaCookie = CookieStorage.get("MCPhase2Media");
    if (mcPhase2MediaCookie) {
      setMCPhase2MediaOn(!!mcPhase2MediaCookie);
    }

    const liveTranslationCookie = CookieStorage.get("liveTranslation");
    if (liveTranslationCookie) {
      setLiveTranslationOn(!!liveTranslationCookie);
    }

    if (isFaceBlurFeatureFlag) {
      setFaceBlurFeatureMode("mobile");
    } else {
      const faceBlurFeatureCookie = CookieStorage.get("faceBlurFeatureVisible");
      if (faceBlurFeatureCookie) {
        const cookieVal = String(faceBlurFeatureCookie);
        // Migrate legacy boolean "true" values to "mobile"
        setFaceBlurFeatureMode(cookieVal === "true" ? "mobile" : cookieVal);
      }
    }
  }, [isFaceBlurFeatureFlag]);

  return (
    <>
      <div className='FeatureSwitch__Cobrand-item'>
        <TailwindSwitch
          size={"xs"}
          label={"Message Center Phase 2 Video and Audio"}
          onLabel={"On"}
          offLabel={"Off"}
          onChange={toggleMCPhase2Media}
          isChecked={mcPhase2MediaOn}
        />
      </div>
      <div className='FeatureSwitch__Cobrand-item'>
        <TailwindSwitch
          size={"xs"}
          label={"Bulk Messaging"}
          onLabel={"On"}
          offLabel={"Off"}
          onChange={toggleBulkMessage}
          isChecked={bulkMessageOn}
        />
      </div>
      <div className='FeatureSwitch__Cobrand-item'>
        <TailwindSwitch
          size={"xs"}
          label={"Live Translation"}
          onLabel={"On"}
          offLabel={"Off"}
          onChange={toggleLiveTranslate}
          isChecked={liveTranslationOn}
        />
      </div>
      <div
        className='FeatureSwitch__Cobrand-item'
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}>
        <span className='text-lg font-bold'>
          Face Blur (Broadcaster)
          {isFaceBlurFeatureFlag && (
            <span className='text-xs font-normal ml-2'>(API flag active)</span>
          )}
        </span>
        <TailwindDropdown
          shape={"rounded"}
          backgroundColor={"primary"}
          name={"faceBlurFeature"}
          value={faceBlurFeatureMode}
          disabled={isFaceBlurFeatureFlag}
          onChange={onChangeFaceBlurFeature}
          items={[
            { label: "Off", value: "off" },
            { label: "Mobile Only", value: "mobile" },
            { label: "Mobile + Desktop", value: "all" },
          ]}
        />
      </div>
    </>
  );
};

export default inject("broadcastStore")(observer(UpcomingFeatures));
